import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Select, FormControl, InputLabel, Box, Slider, Typography } from '@mui/material';
import { CreateOrderPlanItemPayload, OrderPlanType } from '../../services/orderPlanService';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateOrderPlanItemPayload) => Promise<void> | void;
  projectId: string;
  initial?: Partial<{
    type: OrderPlanType;
    description: string;
    lead_time_days: number;
    deadline_date: string | null;
    status: 'zbieranie ofert' | 'rozbieżności projektowe' | 'negocjacje' | 'zamówiono';
    order_date: string | null;
  }>;
}

const AddOrderPlanItemDialog: React.FC<Props> = ({ open, onClose, onSubmit, projectId, initial }) => {
  const [type, setType] = useState<OrderPlanType>('material');
  const [description, setDescription] = useState('');
  const [leadTimeDays, setLeadTimeDays] = useState<number>(0);
  const [leadTimeWeeks, setLeadTimeWeeks] = useState<number>(0); // suwak w tygodniach
  const [deadline, setDeadline] = useState<string | null>(null);
  const [status, setStatus] = useState<'zbieranie ofert' | 'rozbieżności projektowe' | 'negocjacje' | 'zamówiono'>('zbieranie ofert');
  const [orderDate, setOrderDate] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setType(initial?.type || 'material');
      setDescription(initial?.description || '');
      const initDays = initial?.lead_time_days ?? 0;
      setLeadTimeDays(initDays);
      setLeadTimeWeeks(Math.round(initDays / 7));
      setDeadline(initial?.deadline_date ?? null);
      setStatus(initial?.status || 'zbieranie ofert');
      setOrderDate(initial?.order_date ?? null);
    }
  }, [open, initial]);

  const deliveryPreview = useMemo(() => {
    const today = new Date();
    const d = new Date(today);
    d.setDate(today.getDate() + (Number.isFinite(+leadTimeDays) ? leadTimeDays : 0));
    return d.toISOString().slice(0, 10);
  }, [leadTimeDays]);

  const handleSubmit = async () => {
    const payload: CreateOrderPlanItemPayload = {
      project_id: projectId,
      type,
      description,
      lead_time_days: leadTimeDays,
      deadline_date: deadline ?? null,
      status,
      order_date: status === 'zamówiono' ? (orderDate || null) : null
    };
    await onSubmit(payload);
    onClose();
  };

  const valid = description.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Edytuj pozycję planu zamówień' : 'Dodaj pozycję planu zamówień'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: 2, mb: 2 }}>
          <FormControl size="small">
            <InputLabel id="opl-type">Typ</InputLabel>
            <Select labelId="opl-type" value={type} label="Typ" onChange={(e) => setType(e.target.value as OrderPlanType)}>
              <MenuItem value="material">materiał</MenuItem>
              <MenuItem value="service">usługa</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Opis" value={description} onChange={(e) => setDescription(e.target.value)} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary">Czas realizacji (tygodnie)</Typography>
          <Box sx={{ px: 1 }}>
            <Slider value={leadTimeWeeks} onChange={(_, v) => {
              const weeks = Array.isArray(v) ? v[0] : (v as number);
              setLeadTimeWeeks(weeks);
              setLeadTimeDays(weeks * 7);
            }} step={1} min={0} max={50} valueLabelDisplay="auto" />
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
          <TextField size="small" type="number" label="Czas realizacji (dni)" value={leadTimeDays}
            onChange={(e) => {
              const days = parseInt(e.target.value || '0', 10);
              setLeadTimeDays(days);
              setLeadTimeWeeks(Math.round(days / 7));
            }} />
          <TextField size="small" type="date" label="Termin (deadline)" value={deadline ?? ''}
            onChange={(e) => setDeadline(e.target.value || null)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="Termin dostawy (podgląd)" value={deliveryPreview} InputProps={{ readOnly: true }} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <FormControl size="small">
            <InputLabel id="opl-status">Status</InputLabel>
            <Select labelId="opl-status" value={status} label="Status" onChange={(e) => setStatus(e.target.value as any)}>
              <MenuItem value="zbieranie ofert">zbieranie ofert</MenuItem>
              <MenuItem value="rozbieżności projektowe">rozbieżności projektowe</MenuItem>
              <MenuItem value="negocjacje">negocjacje</MenuItem>
              <MenuItem value="zamówiono">zamówiono</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="Termin zamówienia" value={orderDate ?? ''} onChange={(e) => setOrderDate(e.target.value || null)} InputLabelProps={{ shrink: true }} disabled={status !== 'zamówiono'} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Anuluj</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!valid}>Dodaj</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOrderPlanItemDialog;


